import { BlockEntity } from "Backend";
import { Block, ClusterType, FormField, PageType } from "generated/graphql";
import { FC, useEffect, useState } from "react";
import { UseFormCard } from "../useFormCard";
import getFieldCellValues from "./getFieldCellValues";
import { isFormValueAnswered } from "helpers/isFormValueAnswered";

interface SyncFormGroupValuesProps {
  fieldId: number | undefined;
  block: Block | undefined;
  useFormCard: UseFormCard;
  clusterType?: ClusterType;
  pageType?: PageType;
  entity?: BlockEntity;
  lensRuleDescription?: string;
  lensRuleSubtype?: string;
  docPageCount: number;
  children: React.ReactNode;
}

const SyncFormGroupValues: FC<SyncFormGroupValuesProps> = ({
  fieldId,
  block,
  useFormCard,
  clusterType,
  pageType,
  entity,
  lensRuleDescription,
  lensRuleSubtype,
  children,
}) => {
  const {
    form,
    serverValues,
    formGroupValuesState,
    fieldAnsweredState,
    formModeState,
    selectedValueGroupState,
    pageRangeState,
  } = useFormCard;
  const [selectedValueGroup] = selectedValueGroupState;
  const [, setGroup] = formGroupValuesState;
  const [, setAnswered] = fieldAnsweredState;
  const field: FormField | undefined = form?.fields.find((f) => f.id === fieldId);
  const [, setReferenceType] = useState<"page" | "page_range">("page");
  const fieldValue = serverValues.find((v) => v.fieldId === field?.id);
  const isRepeating = Boolean(field?.repeating);
  const isAnswered = field !== undefined ? isFormValueAnswered(field, fieldValue) : false;

  const syncFormWithValues = () => {
    if (!form || !serverValues || !field) {
      return;
    }
    pageRangeState[1](undefined);
    setReferenceType("page");
    if (field.repeating && field.promptYesNo) {
      setAnswered(true);
    } else {
      setAnswered(fieldValue?.yesNoValue || undefined);
    }

    if (!isRepeating && isAnswered && fieldValue) {
      const existingValueGroup = fieldValue.valueGroups[0];
      const isYesServer = fieldValue.yesNoValue === true;
      if (isYesServer && existingValueGroup) {
        setGroup(existingValueGroup.values);
        return;
      }
    }

    // User has specified to edit an existing response
    if (selectedValueGroup !== -1 && fieldValue) {
      const existingValueGroup = fieldValue.valueGroups[selectedValueGroup];
      const isYesServer = fieldValue.yesNoValue === true;
      if (isYesServer && existingValueGroup) {
        setGroup(existingValueGroup.values);
        return;
      }
    }

    setGroup(
      getFieldCellValues({
        cells: field.cells,
        block,
        entity: entity,
        pageType: pageType,
        clusterType: clusterType,
        lensRuleDescription: lensRuleDescription,
        lensRuleSubtype: lensRuleSubtype,
      })
    );
  };

  useEffect(() => {
    syncFormWithValues();
  }, [fieldId, selectedValueGroup, block?.index, formModeState[0]]);

  return <>{children}</>;
};

export default SyncFormGroupValues;
